import { parseNumber } from "../../services/currencyService";

/**
 * תצוגת סכום לפי מטבע ההזמנה (לא convertPrice שמניח ₪).
 * USD + שער: 193$ (702₪) — דולר ראשון, שקל בסוגריים.
 */
export function formatOrderPrice(amount, currency, rate) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  const c = (currency || "ILS").toString().toUpperCase();
  const r =
    rate != null && rate !== "" && String(rate) !== "undefined"
      ? parseFloat(rate)
      : NaN;

  if (c === "USD") {
    if (!Number.isNaN(r) && r > 0) {
      const ils = n * r;
      return `${parseNumber(n)}$ (${parseNumber(Math.round(ils))}₪)`;
    }
    return `${parseNumber(n)}$`;
  }

  return `${parseNumber(n)}₪`;
}

/**
 * כרטיס הזמנה: סימן מטבע לפי סוג ההזמנה בלבד (ללא ₪ כברירה על USD).
 */
export function formatOrderCardAmount(amount, currency) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  const c = (currency || "ILS").toString().toUpperCase();
  if (c === "USD") return `${parseNumber(n)}$`;
  return `${parseNumber(n)}₪`;
}

/** שער מה-localStorage (אותו מפתח כמו convertPrice) */
export function getStoredUsdIlsRate() {
  try {
    const rate = localStorage.getItem("currentRate");
    if (rate && rate !== "undefined") return rate;
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * שער USD→ILS לתצוגה ולפעולות סוכן: מעדיף ערך מההקשר (AppContext / אדמין),
 * אחרת localStorage מהבורר, אחרת null.
 * @param {unknown} preferredRate - למשל currencyRate.USD מ־AppContext
 * @returns {number|null}
 */
export function resolveUsdIlsRateNumber(preferredRate) {
  const p =
    preferredRate != null && preferredRate !== ""
      ? Number(preferredRate)
      : NaN;
  if (Number.isFinite(p) && p > 0) return p;
  const s = getStoredUsdIlsRate();
  const n =
    s != null && s !== "" && String(s) !== "undefined" ? parseFloat(String(s)) : NaN;
  if (Number.isFinite(n) && n > 0) return n;
  return null;
}

/**
 * GUARD — מתי מחזירים null (מטבע תשלום לא ברור להזמנת USD):
 * - הזמנה ב-USD (currency / packagepricedetails.Currency)
 * - ואין sumPaidUsd / paidUsd מה-API
 * - ואין מערך payments, או שבתשלומים ששולמו אין Currency/AmountILS/AmountUSD ברור
 *   (למשל רק Amount מספרי בלי מטבע = לא יודעים אם זה USD או ₪)
 *
 * מחזיר 'ILS' | 'USD' | null. הזמנה שאינה USD → תמיד 'ILS' לתשלומים טיפוסיים.
 */
export function inferPaymentDisplayCurrency(order) {
  if (!order || typeof order !== "object") return "ILS";
  const cur = (
    order.currency ||
    order.packagepricedetails?.Currency ||
    "ILS"
  )
    .toString()
    .toUpperCase();
  if (cur !== "USD") return "ILS";

  const usdExplicit =
    order.sumPaidUsd ?? order.paidUsd ?? order.sumpaid_usd ?? order.sum_paid_usd;
  if (
    usdExplicit != null &&
    String(usdExplicit).trim() !== "" &&
    Number.isFinite(Number(usdExplicit)) &&
    Number(usdExplicit) >= 0
  ) {
    return "USD";
  }

  const pay = order.payments;
  if (!Array.isArray(pay) || pay.length === 0) {
    const sp = parseFloat(order.sumpaid);
    if (!Number.isFinite(sp) || sp === 0) return "ILS";
    return null;
  }

  const paidRows = pay.filter(
    (p) =>
      p && (p.paid === true || p.paid === 1 || p.Paid === true || p.Paid === 1)
  );
  if (paidRows.length === 0) {
    const sp = parseFloat(order.sumpaid);
    if (!Number.isFinite(sp) || sp === 0) return "ILS";
    return null;
  }

  let sawIls = false;
  let sawUsd = false;
  let ambiguous = false;

  for (const p of paidRows) {
    const cy = (p.Currency || p.currency || "").toString().toUpperCase();
    if (cy === "ILS" || cy === "NIS" || p.AmountILS != null) sawIls = true;
    if (cy === "USD" || p.AmountUSD != null) sawUsd = true;
    const hasGeneric =
      p.Amount != null || p.amount != null;
    if (hasGeneric && !cy && p.AmountILS == null && p.AmountUSD == null) {
      ambiguous = true;
    }
  }

  if (ambiguous && !sawIls && !sawUsd) return null;
  if (sawIls && !sawUsd) return "ILS";
  if (sawUsd && !sawIls) return "USD";
  if (sawIls && sawUsd) return null;
  return null;
}

export const ORDER_AMOUNT_UNAVAILABLE_HE = "לא זמין";

/**
 * שולם / סכומי תשלום כשמטבע ההזמנה USD: לא להציג dual מטעה כשלא יודעים אם הסכום ב-USD או ב-₪.
 */
export function formatPaidDisplay(amount, orderCurrency, rate, rawOrder) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  const c = (orderCurrency || "ILS").toString().toUpperCase();
  if (c !== "USD") {
    return formatOrderPrice(n, "ILS", rate);
  }
  if (n === 0) return formatOrderPrice(0, "USD", rate);

  const inferred = inferPaymentDisplayCurrency(rawOrder);
  if (inferred === null) return ORDER_AMOUNT_UNAVAILABLE_HE;
  if (inferred === "ILS") return formatOrderPrice(n, "ILS", null);
  return formatOrderPrice(n, "USD", rate);
}

/**
 * יתרה:
 * - הזמנה לא-USD: totalPrice − sumPaid בשקלים.
 * - USD + guard (תשלום לא ברור) ושולם > 0: לא זמין.
 * - USD + שולם 0: סכום כולל בפורמט הזמנה (USD+שער).
 * - USD + תשלום ידוע ב-₪: הפרש בשקלים (כמו ב-normalize לרוב).
 * - USD + תשלום ידוע ב-USD: FinalPrice/total_usd מינוס sumPaidUsd.
 */
export function formatBalanceDisplay(
  totalPrice,
  sumPaid,
  orderCurrency,
  rate,
  rawOrder
) {
  const c = (orderCurrency || "ILS").toString().toUpperCase();
  const tp = Number(totalPrice);
  const sp = Number(sumPaid);
  if (!Number.isFinite(tp) || !Number.isFinite(sp)) return "—";

  if (c !== "USD") {
    return formatOrderPrice(tp - sp, "ILS", rate);
  }

  const paidInf = inferPaymentDisplayCurrency(rawOrder);
  if (paidInf === null && sp !== 0) return ORDER_AMOUNT_UNAVAILABLE_HE;
  if (paidInf === null && sp === 0) {
    const totalUsd = parseFloat(
      rawOrder?.packagepricedetails?.FinalPrice ?? ""
    );
    if (Number.isFinite(totalUsd) && totalUsd > 0) {
      return formatOrderPrice(totalUsd, "USD", rate);
    }
    return formatOrderPrice(tp, "ILS", null);
  }
  if (paidInf === "ILS") {
    return formatOrderPrice(tp - sp, "ILS", null);
  }
  if (paidInf === "USD") {
    const totalUsd = parseFloat(
      rawOrder?.packagepricedetails?.FinalPrice ??
        rawOrder?.total_usd ??
        rawOrder?.totalPriceUsd ??
        ""
    );
    const paidUsd = parseFloat(
      rawOrder?.sumPaidUsd ?? rawOrder?.paidUsd ?? ""
    );
    if (Number.isFinite(totalUsd) && Number.isFinite(paidUsd)) {
      return formatOrderPrice(totalUsd - paidUsd, "USD", rate);
    }
    return ORDER_AMOUNT_UNAVAILABLE_HE;
  }
  return ORDER_AMOUNT_UNAVAILABLE_HE;
}
